// Graceful AI-unavailable state — the frontend half.
//
// The API answers any AI-engine failure with 503 { error: 'ai_unavailable',
// message: '…' } (see app\module\shell\Service\AiUnavailable). Every AI surface
// used to render `e.response.data.error` verbatim, so an outage read as either a
// raw provider string ("…HTTP 400: your credit balance is too low") or, once the
// backend was tidied, the bare machine code "ai_unavailable". Neither is a
// sentence. This turns the envelope into the copy an operator should see.
//
// Framework-free on purpose: the drafts wizard, the refine rail, the targeting
// step, the landing-copy builder and the lead detail panel all show the same
// words for the same condition.

/** The machine code the API uses. Keep in sync with AiUnavailable::CODE. */
export const AI_UNAVAILABLE = 'ai_unavailable';

/** Fallback copy, for the case where the body never arrived (network drop). */
const AI_UNAVAILABLE_COPY = 'AI features are temporarily unavailable. Please try again later.';

/** Is this caught error the API's AI-unavailable state? */
export function isAiUnavailable(e) {
  const body = e?.response?.data;
  return e?.response?.status === 503 || body?.error === AI_UNAVAILABLE
    ? body?.error === AI_UNAVAILABLE
    : false;
}

/**
 * The message to show for a failed API call.
 *
 * AI-unavailable wins and returns the server's sentence (falling back to our own
 * copy). Everything else keeps the existing precedence — server `error`, then
 * the axios message, then the caller's own fallback — so no non-AI error path
 * changes wording.
 */
export function apiErrorMessage(e, fallback) {
  if (isAiUnavailable(e)) {
    return e?.response?.data?.message || AI_UNAVAILABLE_COPY;
  }
  return e?.response?.data?.error || e?.message || fallback;
}
